import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, FileCheck, Lock, Brain,
  ChevronDown, ChevronRight, Menu, X, Zap, Layers,
  CheckCircle2,
} from "lucide-react";
import { getToken, clearSession } from "../api/session";

/* ─── Logo mark ─────────────────────────────────────────────── */
function LogoMark() {
  return (
    <img
      src="./v_two_white.png"
      alt="OnePass Logo"
      style={{
        width: "160px",
        height: "auto",
        objectFit: "contain",
        display: "block",
        filter: "drop-shadow(0 4px 10px rgba(0,85,255,.25))", borderRadius: "10%",
      }}
    />
  );
}

/* ─── Ripple button ─────────────────────────────────────────── */
function RippleButton({ children, className, style, onClick }) {
  const ref = useRef(null);
  const handle = (e) => {
    const btn = ref.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const d = Math.max(rect.width, rect.height);
      const span = document.createElement("span");
      span.className = "op-ripple";
      span.style.width = span.style.height = `${d}px`;
      span.style.left = `${e.clientX - rect.left - d / 2}px`;
      span.style.top = `${e.clientY - rect.top - d / 2}px`;
      btn.appendChild(span);
      setTimeout(() => span.remove(), 650);
    }
    onClick?.();
  };
  return <button ref={ref} className={className} style={style} onClick={handle}>{children}</button>;
}

/* ─── Navbar (same as LandingPage) ─────────────────────────── */
function Navbar() {
  const navigate = useNavigate();
  const token = getToken();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => { clearSession(); navigate("/build/login"); };

  return (
    <nav className={`op-nav ${scrolled ? "scrolled" : ""}`} style={{ justifyContent: "center", display: "flex" }}>
      <div className="op-nav-inner" style={{ paddingLeft: 24, paddingRight: 16, marginLeft: 16, marginRight: 16, width: "100%" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#001645" }}>
          <LogoMark />
          {/* <span style={{ fontWeight: 800, fontSize: 19, letterSpacing: "-0.02em" }}>OnePass</span> */}
        </Link>
        <div className="op-nav-links" style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {["Features","How it Works","Why OnePass","Pricing"].map((l) => (
            <Link key={l} to={`/#${l.toLowerCase().replace(/\s/g, "-")}`} className="op-nav-link">{l}</Link>
          ))}
          <Link to="/legal" className="op-nav-link" style={{ color: "#0055FF", fontWeight: 600 }}>Legal</Link>
        </div>
        <div className="op-nav-buttons" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {token ? (
            <>
              <button onClick={handleLogout} className="op-btn-secondary op-nav-btn" style={{ padding: "11px 22px", fontSize: 14 }}>Logout</button>
              <RippleButton onClick={() => navigate("/build")} className="op-btn-primary op-grad-anim op-nav-btn" style={{ padding: "11px 22px", fontSize: 14, gap: 6, background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)" }}>
                Build Code <ArrowRight size={16} />
              </RippleButton>
            </>
          ) : (
            <>
              <button onClick={() => navigate("/build/login")} className="op-btn-secondary op-nav-btn" style={{ padding: "11px 22px", fontSize: 14 }}>Sign In</button>
              <RippleButton onClick={() => navigate("/build/register")} className="op-btn-primary op-grad-anim op-nav-btn" style={{ padding: "11px 22px", fontSize: 14, gap: 6, background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)" }}>
                Sign Up <ArrowRight size={16} />
              </RippleButton>
            </>
          )}
        </div>
        <button className="op-menu-icon" onClick={() => setMobileMenuOpen(true)}><Menu size={26} /></button>
      </div>
      <div className={`op-mobile-menu-backdrop${mobileMenuOpen ? " open" : ""}`} onClick={() => setMobileMenuOpen(false)} />
      <div className={`op-mobile-menu${mobileMenuOpen ? " open" : ""}`}>
        <div className="op-mobile-menu-header">
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#001645" }} onClick={() => setMobileMenuOpen(false)}>
            <LogoMark /><span style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em" }}>OnePass</span>
          </Link>
          <button className="op-mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu"><X size={18} /></button>
        </div>
        <nav className="op-mobile-nav-links">
          {[{label:"Features",icon:<Zap size={16}/>},{label:"How it Works",icon:<Layers size={16}/>},{label:"Why OnePass",icon:<ShieldCheck size={16}/>},{label:"Pricing",icon:<CheckCircle2 size={16}/>}].map(({label,icon})=>(
            <Link key={label} to={`/#${label.toLowerCase().replace(/\s/g,"-")}`} className="op-mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <span style={{ color: "var(--op-blue)", opacity: 0.75 }}>{icon}</span>{label}
            </Link>
          ))}
          <Link to="/legal" className="op-mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <span style={{ color: "var(--op-blue)", opacity: 0.75 }}><Lock size={16} /></span>Legal
          </Link>
        </nav>
        <div className="op-mobile-nav-divider" />
        <div className="op-mobile-menu-actions">
          {token ? (
            <>
              <RippleButton onClick={() => { setMobileMenuOpen(false); navigate("/build"); }} className="op-btn-primary op-grad-anim" style={{ padding: "14px 22px", fontSize: 15, width: "100%", background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)" }}>Build Code <ArrowRight size={17} /></RippleButton>
              <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="op-btn-secondary" style={{ padding: "13px 22px", fontSize: 15, width: "100%" }}>Logout</button>
            </>
          ) : (
            <>
              <RippleButton onClick={() => { setMobileMenuOpen(false); navigate("/build/register"); }} className="op-btn-primary op-grad-anim" style={{ padding: "14px 22px", fontSize: 15, width: "100%", background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)" }}>Sign Up <ArrowRight size={17} /></RippleButton>
              <button onClick={() => { setMobileMenuOpen(false); navigate("/build/login"); }} className="op-btn-secondary" style={{ padding: "13px 22px", fontSize: 15, width: "100%" }}>Sign In</button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

/* ─── Footer (same as LandingPage) ─────────────────────────── */
function Footer() {
  const navigate = useNavigate();
  const links = [
    { label: "Features", to: "/#features" },
    { label: "Pricing", to: "/#pricing" },
    { label: "Terms of Service", to: "/terms-of-service" },
    { label: "Changelog", to: "#" },
    { label: "Roadmap", to: "#" },
  ];
  return (
    <footer style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "40px 0 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24, paddingBottom: 28 }}>
          <div>
            <div style={{ display: "flex", alignItems: "start", gap: 4, marginBottom: 10, flexDirection: "column",  }}>
              <LogoMark size={80} />
              <div>
                {/* <div style={{ fontSize: 22, fontWeight: 800, color: "#001645", lineHeight: 1.1 }}>OnePass</div> */}
                <div style={{ fontSize: 11, fontWeight: 600, color: "#0055FF", marginTop: 4, letterSpacing: "0.02em" }}>Powered by OnePassBlueprint.com</div>
              </div>
            </div>
            <p style={{ color: "#667085", fontSize: 14, margin: 0 }}>Validated build blueprints for the AI coding era.</p>
          </div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            {links.map((item) => (
              <Link key={item.label} to={item.to} style={{ color: "#667085", textDecoration: "none", fontSize: 14, fontWeight: 500 }}>{item.label}</Link>
            ))}
          </div>
        </div>
        <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <span style={{ color: "#98A2B3", fontSize: 13 }}>© 2026 OnePass. All rights reserved.</span>
          <span style={{ color: "#0055FF", fontSize: 13, fontWeight: 600 }}>Fix it before you build it.</span>
        </div>
      </div>
    </footer>
  );
}

/* ─── Section data ──────────────────────────────────────────── */
const SECTIONS = [
  {
    id: "free-trial", icon: FileCheck, color: "#0055FF", bg: "rgba(0,85,255,.08)",
    title: "Free Trial",
    summary: "Up to 3 blueprints · 30-day period · As-is basis",
    body: [
      "The free trial includes up to three blueprints during a 30-day period.",
      `The trial and all resulting outputs are provided on an "as-is" and "as-available" basis, without warranties or guarantees regarding accuracy, completeness, availability, or suitability for any particular purpose.`,
    ],
  },
  {
    id: "ai-disclosure", icon: Brain, color: "#7C3AED", bg: "rgba(124,58,237,.08)",
    title: "Artificial Intelligence Disclosure",
    summary: "AI-generated outputs · Human review required · No professional advice",
    body: [
      "OnePass uses artificial intelligence and automated validation tools to generate, analyze, and review blueprints and related outputs. Although the service is designed to improve accuracy and identify potential issues, AI-generated and automated outputs may contain errors, omissions, or unexpected results.",
      "You are responsible for reviewing, testing, validating, and approving all outputs before using them for development, production, business operations, or any other purpose. OnePass does not provide legal, financial, medical, engineering, or other licensed professional advice.",
    ],
  },
  {
    id: "accounts-payments", icon: Lock, color: "#059669", bg: "rgba(5,150,105,.08)",
    title: "Accounts and Payments",
    summary: "No card for free trial · Secure payment processing · No stored card data",
    body: [
      "No account registration or billing information is required to use the free trial.",
      "Users who purchase Blueprint Credits or other paid services must create an account and provide limited information necessary to register and manage the account, such as their name and contact information. A $5 payment provides the number of Blueprint Credits displayed at the time of purchase.",
      "Payment and credit-card information is securely collected and processed by an independent third-party payment provider and is subject to that provider's terms and privacy practices. OnePass does not directly store your complete credit-card number or card security code.",
      "We may retain limited account and transaction information necessary to manage your account, credits, purchases, customer support, platform security, and legal or recordkeeping obligations.",
    ],
  },
  {
    id: "submitted-content", icon: ShieldCheck, color: "#0891B2", bg: "rgba(8,145,178,.08)",
    title: "Submitted Content and Privacy",
    summary: "Data minimisation · No selling · Third-party processors limited",
    body: [
      "The service processes the instructions, specifications, files, and other content you voluntarily submit to generate the requested output and to operate, secure, and support the platform.",
      "We do not retain submitted project content longer than reasonably necessary to provide the requested service, maintain security, prevent misuse, resolve technical issues, or comply with applicable legal obligations.",
      "You are responsible for ensuring that you have the right and authorization to submit all content provided to the service. Do not submit highly sensitive, confidential, regulated, or personally identifiable information unless you are authorized to do so and the service has been approved for that type of information.",
      "Limited information may be processed by third-party service providers that support payment processing, account management, hosting, security, artificial intelligence, and operation of the service.",
      "We do not sell personal information or share it with third parties for their independent advertising purposes.",
    ],
  },
];

/* ─── Reveal hook ───────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ─── Accordion card ────────────────────────────────────────── */
function Card({ section, index }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useReveal();
  const Icon = section.icon;
  return (
    <div
      ref={ref}
      id={section.id}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity .55s cubic-bezier(.22,1,.36,1) ${index * 80}ms, transform .55s cubic-bezier(.22,1,.36,1) ${index * 80}ms`,
        background: "#fff",
        borderRadius: 20,
        border: `1px solid ${open ? section.color + "44" : "#E5E7EB"}`,
        boxShadow: open ? `0 12px 40px ${section.color}18` : "0 2px 10px rgba(0,22,69,.05)",
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "22px 26px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
      >
        <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: open ? section.color : section.bg, color: open ? "#fff" : section.color, transition: "background .25s, color .25s" }}>
          <Icon size={20} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: open ? section.color : "#001645", transition: "color .2s", marginBottom: 3 }}>{section.title}</div>
          {!open && <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{section.summary}</div>}
        </div>
        <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: open ? section.color + "18" : "rgba(0,22,69,.06)", color: open ? section.color : "#94A3B8", transition: "all .25s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <ChevronDown size={15} />
        </span>
      </button>
      <div style={{ maxHeight: open ? 900 : 0, overflow: "hidden", transition: "max-height .45s cubic-bezier(.22,1,.36,1)" }}>
        <div style={{ padding: "16px 26px 26px", borderTop: `1px solid ${section.color}22` }}>
          {section.body.map((para, i) => (
            <p key={i} style={{ margin: i === 0 ? 0 : "12px 0 0", fontSize: 14, color: "#4B5563", lineHeight: 1.8 }}>{para}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Agreement banner ──────────────────────────────────────── */
function AgreementBanner() {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "opacity .6s .1s, transform .6s .1s", marginTop: 8, background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)", borderRadius: 20, padding: "32px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 80% 50%, rgba(255,255,255,.08), transparent 60%)", pointerEvents: "none" }} />
      <span style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,.15)", color: "#fff" }}>
        <ShieldCheck size={22} />
      </span>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#fff", marginBottom: 4 }}>Your agreement</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,.75)", lineHeight: 1.6 }}>By using the free trial, creating an account, or purchasing a paid service, you agree to the applicable Terms of Use and Privacy Policy.</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.5)", display: "flex", alignItems: "center", gap: 6 }}>
        <ChevronRight size={13} /> Effective 2026
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */
export default function LegalPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <style>{`
        :root {
          --op-blue: #0055FF; --op-heading: #001645;
          --op-border: #E5E7EB; --op-accent: #00B6FF;
          --op-grad: linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%);
          --op-grad-accent: linear-gradient(135deg,#0055FF,#00B6FF);
        }
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; font-family: 'Inter', system-ui, sans-serif; }

        /* shared nav styles */
        .op-nav { position:fixed; top:0; left:0; right:0; z-index:50; transition: all .4s cubic-bezier(.22,1,.36,1); display:flex; justify-content:center; }
        .op-nav-inner { display:flex; align-items:center; justify-content:space-between; padding:14px 28px; margin:12px auto; max-width:1200px; border-radius:20px; transition:all .4s; background:rgba(255,255,255,.55); backdrop-filter:blur(6px) saturate(150%); border:1px solid rgba(255,255,255,.6); }
        .op-nav.scrolled .op-nav-inner { background:rgba(255,255,255,.78); backdrop-filter:blur(20px) saturate(180%); box-shadow:0 8px 32px rgba(0,22,69,.10); border-color:var(--op-border); }
        .op-nav-link { font-size:14px; font-weight:500; color:#3a4a6b; text-decoration:none; padding:8px 12px; border-radius:10px; transition:all .25s; }
        .op-nav-link:hover { color:var(--op-blue); background:rgba(0,85,255,.06); }
        .op-btn-primary { position:relative; overflow:hidden; cursor:pointer; border:none; background:var(--op-grad); color:#fff; font-family:inherit; font-weight:600; border-radius:14px; display:inline-flex; align-items:center; gap:8px; justify-content:center; transition:transform .25s,box-shadow .25s; box-shadow:0 8px 24px rgba(0,85,255,.35); }
        .op-btn-primary:hover { transform:translateY(-2px) scale(1.02); box-shadow:0 14px 40px rgba(0,85,255,.5); }
        .op-btn-secondary { cursor:pointer; background:#fff; color:var(--op-heading); border:1px solid var(--op-border); font-family:inherit; font-weight:600; border-radius:14px; display:inline-flex; align-items:center; gap:8px; justify-content:center; transition:all .25s; box-shadow:0 2px 10px rgba(0,22,69,.06); }
        .op-btn-secondary:hover { border-color:var(--op-accent); transform:translateY(-2px); }
        @keyframes op-ripple { to { transform:scale(4); opacity:0; } }
        .op-ripple { position:absolute; border-radius:50%; background:rgba(255,255,255,.5); transform:scale(0); animation:op-ripple .6s ease-out; pointer-events:none; }
        @keyframes op-grad-move { 0%,100%{background-position:0% 50%;} 50%{background-position:100% 50%;} }
        .op-grad-anim { background-size:200% 200%; animation:op-grad-move 10s ease infinite; }

        /* mobile nav */
        .op-menu-icon { display:none; }
        .op-mobile-menu-backdrop { display:none; position:fixed; inset:0; z-index:99; background:rgba(0,22,69,.35); backdrop-filter:blur(4px); opacity:0; transition:opacity .35s; }
        .op-mobile-menu-backdrop.open { opacity:1; }
        .op-mobile-menu { display:none; position:fixed; top:0; right:0; bottom:0; width:min(320px,88vw); z-index:100; flex-direction:column; background:rgba(255,255,255,.92); backdrop-filter:blur(28px) saturate(180%); border-left:1px solid rgba(255,255,255,.75); box-shadow:-12px 0 56px rgba(0,22,69,.18); transform:translateX(100%); transition:transform .38s cubic-bezier(.22,1,.36,1); overflow-y:auto; }
        .op-mobile-menu.open { display:flex !important; transform:translateX(0); }
        .op-mobile-menu-header { display:flex; align-items:center; justify-content:space-between; padding:20px 22px 18px; border-bottom:1px solid var(--op-border); }
        .op-mobile-menu-close { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:rgba(0,22,69,.06); border:none; cursor:pointer; color:#001645; transition:background .2s; }
        .op-mobile-nav-links { display:flex; flex-direction:column; padding:16px 16px 8px; gap:2px; }
        .op-mobile-nav-link { display:flex; align-items:center; gap:10px; font-size:15px; font-weight:500; color:#3a4a6b; text-decoration:none; padding:13px 14px; border-radius:12px; transition:all .2s; }
        .op-mobile-nav-link:hover { color:var(--op-blue); background:rgba(0,85,255,.07); }
        .op-mobile-nav-divider { height:1px; background:var(--op-border); margin:8px 16px; }
        .op-mobile-menu-actions { display:flex; flex-direction:column; padding:8px 16px 24px; gap:10px; }
        @media (max-width:789px) {
          .op-menu-icon { display:flex !important; align-items:center; justify-content:center; width:38px; height:38px; border-radius:10px; background:rgba(0,22,69,.06); border:none; cursor:pointer; color:#001645; padding:0; }
          .op-nav-links { display:none !important; }
          .op-nav-buttons { display:none !important; }
          .op-mobile-menu-backdrop.open { display:block !important; }
        }
        @media (max-width:520px) {
          .op-nav-inner { padding:10px 16px !important; }
          .op-nav-btn { padding:6px 10px !important; font-size:12px !important; }
        }

        /* page */
        .lp-legal-page { background:#F8FAFF; min-height:100vh; }
        @keyframes lp-fade-up { from{opacity:0;transform:translateY(28px);} to{opacity:1;transform:translateY(0);} }
        .lp-fu1 { animation:lp-fade-up .7s cubic-bezier(.22,1,.36,1) .05s both; }
        .lp-fu2 { animation:lp-fade-up .7s cubic-bezier(.22,1,.36,1) .2s both; }
        .lp-fu3 { animation:lp-fade-up .7s cubic-bezier(.22,1,.36,1) .35s both; }
        .lp-fu4 { animation:lp-fade-up .7s cubic-bezier(.22,1,.36,1) .5s both; }
        .lp-grad-text { background:linear-gradient(93deg,#0D47A1 0%,#0055FF 45%,#00B6FF 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        @keyframes lp-orb-a { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-20px) scale(1.08);} }
        @keyframes lp-orb-b { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-20px,30px) scale(1.05);} }
        .lp-orb-a { animation:lp-orb-a 12s ease-in-out infinite; }
        .lp-orb-b { animation:lp-orb-b 15s ease-in-out infinite; }
        .lp-trust-chip { display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:600; color:#334155; background:rgba(255,255,255,.85); border:1px solid #E2E8F0; border-radius:20px; padding:7px 14px; backdrop-filter:blur(8px); box-shadow:0 2px 8px rgba(0,22,69,.04); }
        @media (min-width:1100px) { .lp-legal-layout { display:grid; grid-template-columns:220px 1fr; gap:48px; align-items:start; } .lp-toc { position:sticky; top:110px; } }
        @media (max-width:1099px) { .lp-legal-layout { display:block; } .lp-toc { display:none; } }
        @media (max-width:768px) {
          .lp-hero-section { padding: 100px 20px 60px !important; }
          .lp-trust-chip { font-size: 11px !important; padding: 6px 11px !important; }
        }
        @media (max-width:480px) {
          .lp-hero-section { padding: 90px 16px 48px !important; }
          .lp-hero-h1 { white-space: normal !important; font-size: clamp(24px,8vw,36px) !important; }
          .lp-trust-chip { font-size: 11px !important; padding: 5px 10px !important; }
        }
      `}</style>

      <div className="lp-legal-page">
        <Navbar />

        {/* ── HERO ──────────────────────────────────────────── */}
        <div className="lp-hero-section" style={{ position: "relative", overflow: "hidden", background: "linear-gradient(170deg,#EEF4FF 0%,#F8FAFF 55%,#EAF5FF 100%)", padding: "120px 24px 80px", textAlign: "center" }}>
          {/* grid bg */}
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(0,85,255,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,85,255,.04) 1px,transparent 1px)", backgroundSize: "48px 48px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 0%,black 30%,transparent 100%)" }} />
          <div className="lp-orb-a" style={{ position: "absolute", top: -80, left: "15%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(0,85,255,.12),transparent 70%)", pointerEvents: "none" }} />
          <div className="lp-orb-b" style={{ position: "absolute", bottom: -60, right: "10%", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(0,182,255,.12),transparent 70%)", pointerEvents: "none" }} />

          <div style={{ position: "relative", maxWidth: 720, margin: "0 auto" }}>
            <div className="lp-fu1" style={{ marginBottom: 20 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#0055FF", background: "rgba(0,85,255,.09)", border: "1px solid rgba(0,85,255,.18)", borderRadius: 20, padding: "5px 14px" }}>
                <ShieldCheck size={12} /> Legal &amp; Privacy
              </span>
            </div>
            <h1 className="lp-fu2 lp-hero-h1" style={{ fontSize: "clamp(28px,6vw,60px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.15, margin: "0 0 18px", color: "#001645", whiteSpace: "nowrap" }}>
              Disclaimer &amp; <span className="lp-grad-text">Privacy Notice</span>
            </h1>
            <p className="lp-fu3" style={{ fontSize: 16, color: "#667085", lineHeight: 1.7, margin: "0 auto 36px", maxWidth: 520 }}>
              By using the free trial, creating an account, or purchasing a paid service, you agree to the applicable Terms of Use and Privacy Policy.
            </p>
            <div className="lp-fu4" style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
              {[{dot:"#0055FF",label:"Transparent AI policy"},{dot:"#22C55E",label:"No card for free trial"},{dot:"#F59E0B",label:"Data never sold"},{dot:"#7C3AED",label:"Secure payments"}].map(({dot,label}) => (
                <span key={label} className="lp-trust-chip">
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0, boxShadow: `0 0 0 3px ${dot}22` }} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── CONTENT ───────────────────────────────────────── */}
        <div style={{ maxWidth: 1140, margin: "0 auto", padding: "56px 24px 80px" }}>
          <div className="lp-legal-layout">
            {/* Sticky TOC */}
            <aside className="lp-toc">
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: "20px 0", boxShadow: "0 4px 16px rgba(0,22,69,.06)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#94A3B8", padding: "0 20px 12px", borderBottom: "1px solid #F1F5F9", marginBottom: 8 }}>Contents</div>
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a key={s.id} href={`#${s.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", fontSize: 13, fontWeight: 500, color: "#4B5563", textDecoration: "none", transition: "all .18s", borderLeft: "3px solid transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = s.color; e.currentTarget.style.borderLeftColor = s.color; e.currentTarget.style.background = s.bg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#4B5563"; e.currentTarget.style.borderLeftColor = "transparent"; e.currentTarget.style.background = "transparent"; }}
                    >
                      <Icon size={14} style={{ color: s.color, flexShrink: 0 }} />{s.title}
                    </a>
                  );
                })}
              </div>
            </aside>

            {/* Cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {SECTIONS.map((s, i) => <Card key={s.id} section={s} index={i} />)}
              <AgreementBanner />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
